import React, { useMemo } from 'react';
import {
  ComposedChart, AreaChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  buildMonthlyMetrics,
  fmtUSD,
  fmtHours,
  type MonthlyAggMetrics,
  type EffectiveRecord,
} from '../../../utils/accountOwnerUtils';
import type { RateConfig } from '../../../hooks/useAccountOwnerData';

interface Props {
  effectiveRecords: EffectiveRecord[];
  gradeTechRates: Record<string, number>;
  rateConfig: RateConfig | null;
  clientBillingRateByClientId: Record<string, number>;
  projectToClient: Record<string, { client_id: number | string; client_name: string }>;
  effectiveRateByClient: Record<string, number>;
  fyYear: string;  // e.g. "2025" for FY 2025-26; passed from page filter
}

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtAxis = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
};
const fmtHrAxis = (v: number) =>
  v >= 1_000 ? `${(v / 1_000).toFixed(1)}K h` : `${Math.round(v)} h`;
const fmtPctAxis = (v: number) => `${v.toFixed(0)}%`;
const fmtRateAxis = (v: number) => `$${v.toFixed(0)}/h`;

// ─── Shared tooltip ───────────────────────────────────────────────────────────
const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; unit?: string }>;
  label?: string;
  formatter?: (name: string, value: number) => string;
}> = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[160px]">
      <p className="text-[11px] font-bold text-slate-600 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-none" style={{ backgroundColor: p.color }} />
            <span className="text-[11px] text-slate-600">{p.name}</span>
          </div>
          <span className="text-[11px] font-semibold tabular-nums">
            {formatter ? formatter(p.name, p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Section header ───────────────────────────────────────────────────────────
const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
    <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-slate-100">
      <div className="w-0.5 h-4 rounded-full bg-blue-500 flex-none" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const AXIS_STYLE = { fontSize: 10, fill: '#94A3B8' };

// ─── Main component ───────────────────────────────────────────────────────────
export const AnalyticsChartsSection: React.FC<Props> = ({
  effectiveRecords,
  gradeTechRates,
  rateConfig,
  clientBillingRateByClientId,
  projectToClient,
  effectiveRateByClient,
  fyYear,
}) => {
  const monthly: MonthlyAggMetrics[] = useMemo(() => {
    if (!fyYear) return [];
    return buildMonthlyMetrics(
      effectiveRecords,
      fyYear,
      gradeTechRates,
      rateConfig,
      clientBillingRateByClientId,
      projectToClient,
      effectiveRateByClient
    );
  }, [effectiveRecords, fyYear, gradeTechRates, rateConfig, clientBillingRateByClientId, projectToClient, effectiveRateByClient]);

  if (!fyYear) return null;

  const hasData = monthly.some(m => m.billableHours > 0 || m.revenue > 0);
  const fyLabel = `FY ${fyYear}–${String(Number(fyYear) + 1).slice(2)}`;

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <div className="w-1 h-5 rounded-full bg-violet-500 flex-none" />
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-widest">Monthly Analytics</h2>
        <span className="ml-1 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">· {fyLabel}</span>
      </div>

      {!hasData ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md p-8 text-center text-slate-500 text-sm">
          No data for selected financial year
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

          {/* Chart 1: Revenue = stacked Cost + Profit bars, Revenue line on top */}
          <ChartCard title="Revenue · Cost · Profit">
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={monthly} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="label" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={fmtAxis} tick={AXIS_STYLE} axisLine={false} tickLine={false} width={60} />
                <Tooltip
                  content={<ChartTooltip formatter={(_, v) => fmtUSD(v)} />}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: '#64748B' }} iconType="circle" iconSize={8} />
                {/* Stacked bars: cost + profit = revenue */}
                <Bar dataKey="cost" name="Cost" stackId="rev" fill="#F97316" opacity={0.85} />
                <Bar dataKey="profit" name="Profit" stackId="rev" fill="#10B981" radius={[3, 3, 0, 0]} opacity={0.85} />
                {/* Revenue line on top for reference */}
                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke="#2563EB"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#2563EB', strokeWidth: 0 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Chart 2: Margin % — area chart, zoomed Y-axis, avg reference line */}
          {(() => {
            const validMargins = monthly.map(m => m.marginPct).filter((v): v is number => v != null);
            const minM = Math.max(0, Math.floor((Math.min(...validMargins) - 5) / 5) * 5);
            const maxM = Math.ceil((Math.max(...validMargins) + 5) / 5) * 5;
            const avgM = validMargins.length > 0 ? validMargins.reduce((s, v) => s + v, 0) / validMargins.length : null;
            return (
              <ChartCard title="Margin % · Month by Month">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={monthly} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <defs>
                      <linearGradient id="gradMargin" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="label" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                    <YAxis
                      tickFormatter={fmtPctAxis}
                      tick={AXIS_STYLE}
                      axisLine={false}
                      tickLine={false}
                      width={42}
                      domain={[minM, maxM]}
                    />
                    <Tooltip
                      content={<ChartTooltip formatter={(_, v) => `${v != null ? v.toFixed(1) : '—'}%`} />}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#64748B' }} iconType="circle" iconSize={8} />
                    {avgM != null && (
                      <ReferenceLine
                        y={avgM}
                        stroke="#A78BFA"
                        strokeDasharray="5 3"
                        strokeWidth={1.5}
                        label={{ value: `Avg ${avgM.toFixed(1)}%`, position: 'insideTopRight', fontSize: 9, fill: '#7C3AED', fontWeight: 600 }}
                      />
                    )}
                    <Area
                      type="monotone"
                      dataKey="marginPct"
                      name="Margin %"
                      stroke="#8B5CF6"
                      strokeWidth={2.5}
                      fill="url(#gradMargin)"
                      dot={{ r: 3, fill: '#8B5CF6', strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            );
          })()}

          {/* Chart 3: Effective Billing Rate — area chart, zoomed Y-axis */}
          {(() => {
            const validRates = monthly.map(m => m.effBillingRate).filter((v): v is number => v != null);
            const minR = Math.max(0, Math.floor((Math.min(...validRates) - 5) / 5) * 5);
            const maxR = Math.ceil((Math.max(...validRates) + 5) / 5) * 5;
            const avgR = validRates.length > 0 ? validRates.reduce((s, v) => s + v, 0) / validRates.length : null;
            return (
              <ChartCard title="Effective Billing Rate · Month by Month">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={monthly} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <defs>
                      <linearGradient id="gradRate" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0891B2" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#0891B2" stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="label" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                    <YAxis
                      tickFormatter={fmtRateAxis}
                      tick={AXIS_STYLE}
                      axisLine={false}
                      tickLine={false}
                      width={52}
                      domain={[minR, maxR]}
                    />
                    <Tooltip
                      content={<ChartTooltip formatter={(_, v) => v != null ? `$${v.toFixed(2)}/h` : '—'} />}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#64748B' }} iconType="circle" iconSize={8} />
                    {avgR != null && (
                      <ReferenceLine
                        y={avgR}
                        stroke="#67E8F9"
                        strokeDasharray="5 3"
                        strokeWidth={1.5}
                        label={{ value: `Avg $${avgR.toFixed(0)}/h`, position: 'insideTopRight', fontSize: 9, fill: '#0E7490', fontWeight: 600 }}
                      />
                    )}
                    <Area
                      type="monotone"
                      dataKey="effBillingRate"
                      name="Eff. Rate ($/h)"
                      stroke="#0891B2"
                      strokeWidth={2.5}
                      fill="url(#gradRate)"
                      dot={{ r: 3, fill: '#0891B2', strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            );
          })()}

          {/* Chart 4: Billable Hours — bar with avg reference */}
          {(() => {
            const hrs = monthly.map(m => m.billableHours).filter(v => v > 0);
            const avgH = hrs.length > 0 ? hrs.reduce((s, v) => s + v, 0) / hrs.length : null;
            return (
              <ChartCard title="Billable Hours · Month by Month">
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={monthly} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="label" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtHrAxis} tick={AXIS_STYLE} axisLine={false} tickLine={false} width={55} />
                    <Tooltip
                      content={<ChartTooltip formatter={(_, v) => fmtHours(v)} />}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#64748B' }} iconType="circle" iconSize={8} />
                    {avgH != null && (
                      <ReferenceLine
                        y={avgH}
                        stroke="#FCD34D"
                        strokeDasharray="5 3"
                        strokeWidth={1.5}
                        label={{ value: `Avg ${fmtHours(avgH)}`, position: 'insideTopRight', fontSize: 9, fill: '#B45309', fontWeight: 600 }}
                      />
                    )}
                    <Bar dataKey="billableHours" name="Billable Hours" fill="#F59E0B" radius={[3, 3, 0, 0]} opacity={0.85} />
                    <Line
                      type="monotone"
                      dataKey="billableHours"
                      name="Trend"
                      stroke="#92400E"
                      strokeWidth={2}
                      dot={false}
                      strokeDasharray="4 2"
                      legendType="none"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>
            );
          })()}

        </div>
      )}
    </div>
  );
};

export default AnalyticsChartsSection;
